import { Injectable } from '@angular/core';
import { MatDialogConfig, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ModalDialogFullScreenComponent } from './modal-dialog-full-screen/modal-dialog-full-screen.component';
import { ModalDialogStandardComponent } from './modal-dialog-standard/modal-dialog-standard.component';
import { Subject, Observable } from 'rxjs';
import { KeywordsComponent } from '../document/keywords/keywords.component';
import { RemarksComponent } from '../document/remarks/remarks.component';

@Injectable({
  providedIn: 'root'
})
export class DialogService {

  constructor(private dialog: MatDialog) { }


  /**
   * Opens a standard modal dialog containing the given component and properties
   * 
   * @param component Component to dislay as the body of the dialog
   * @param data Parameters to pass to the component
   */
  openModal(component: any, data: any): MatDialogRef<{}, any> {
    //
    // Open a modal dialog, or a full-screen modal dialog, in the case
    // of small (phone-size) mobile devices. The component parameter will become the body of the dialog.
    //
    const dialogConfig = new MatDialogConfig();
    dialogConfig.autoFocus = true;
    dialogConfig.panelClass = "ot-modal-panel";
    dialogConfig.backdropClass = "ot-modal-panel-backdrop";
    dialogConfig.maxWidth = "64rem";
    dialogConfig.minWidth = "44rem";
    dialogConfig.maxHeight = "42rem";
    dialogConfig.data = data;
    dialogConfig.disableClose = data.disableClose;

    let dialogComponent: any = component;
    // Mobile devices less than 768px open as full-screen 
    if (window.matchMedia("(hover: none) and (max-width: 767px)").matches) {
      dialogConfig.maxWidth = "none"; // Prevent artificial restriction of dialog to 80vw by material dialog
      dialogConfig.minWidth = "none";
      dialogConfig.maxHeight = "none";
      dialogConfig.minHeight = "none";
      dialogConfig.width = "100%";
      dialogConfig.height = "100%"; // Cannot set tablet height to 100% or window will not be centered
    }

    if (window.matchMedia("(hover: hover), (-ms-high-contrast: none), (min--moz-device-pixel-ratio:0)").matches) {
      // Desktop opens at 100% of the available height. This won't be full screen as the parent element
      // height is limited.
      dialogConfig.height = "100%";

      // On desktop, textarea in the component requires minHeight or
      // the dialog elements may get cut off when browser is resized.
      // This only applies to specific modal dialogs that use textarea elements.
      // Normal modal dialogs do not have minHeight. See FILE-7014.
      if (data.component == KeywordsComponent || data.component == RemarksComponent) {
        dialogConfig.minHeight = "34rem"
      }
    }

    // Open the dialog
    return this.dialog.open(dialogComponent, dialogConfig);
  }

  /**
   * Opens a large modal dialog containing the given component and properties
   * 
   * @param component Component to dislay as the body of the dialog
   * @param data Parameters to pass to the component
   * @returns Reference to the newly-opened dialog.
   */
  openLargeModal(component: any, data: any): MatDialogRef<{}, any> {
    //
    // Open a large modal dialog. 
    // The component parameter will become the body of the dialog.
    //
    const dialogConfig = new MatDialogConfig();
    dialogConfig.autoFocus = true;
    dialogConfig.panelClass = "ot-large-modal-panel";
    dialogConfig.backdropClass = "ot-modal-panel-backdrop";
    dialogConfig.data = data;
    dialogConfig.maxWidth = "calc(100% - 2rem)"; // 1rem right and left margins
    dialogConfig.minWidth = "none";
    dialogConfig.maxHeight = "calc(100% - 2rem)"; //1rem top and bottom margins

    // Open the dialog
    return this.dialog.open(component, dialogConfig);
  }

  /**
   * Opens a standard 2-button modal dialog containing the given component and properties
   * 
   * @param title Title of the dialog
   * @param titleShort Short title of the dialog (for small form factors)
   * @param primaryButtonText Primary button text (like Save)
   * @param secondaryButtonText Secondary button text (like Cancel)
   * @param component  Type of component to load into the dialog
   * @param params parameters to pass to the dialog
   */
  openStandardModal(title: string,
    titleShort: string,
    primaryButtonText: string,
    secondaryButtonText: string,
    component: any, params: any): { onPrimary: Observable<any>, onSecondary: Observable<void>, dialog: MatDialogRef<{}, any> } {


    let onPrimary = new Subject<any>();
    let onSecondary = new Subject<void>();

    let data = {
      title: title,
      primary: primaryButtonText,
      secondary: secondaryButtonText,
      component: component,
      onPrimary: onPrimary,
      onSecondary: onSecondary,
      disableClose: true,
      data: params
    }

    //
    // openStandardModal will open a modal standard dialog, or a full-screen modal dialog, in the case
    // of small (phone-size) mobile devices. The component parameter will become the body of the dialog.
    //

    let dialogComponent: any = ModalDialogStandardComponent;
    // Mobile devices less than 768px open as full-screen 
    if (window.matchMedia("(hover: none) and (max-width: 767px)").matches) {
      data.title = titleShort; // Full screen dialog should use the short title
      dialogComponent = ModalDialogFullScreenComponent;
    }

    // Open the dialog
    const dialog = this.openModal(dialogComponent, data);

    return {
      onPrimary: onPrimary.asObservable(),
      onSecondary: onSecondary.asObservable(),
      dialog: dialog
    }
  }

  /**
   * Closes all currently-open dialogs.
   */
  closeAll() {
    this.dialog.closeAll();
  }
}
